import { computed } from "vue";
import store from "@/store";
import { isAncestryKitPending, isUploadWithoutResults, currentKitStep, KIT_FAILURES } from "@/services/kitService";

// The tests that give ancestry results, as the kit's tests name them
const ANCESTRY_TESTS = ["autosomal", "autosomalStarter", "mtdna", "ydna", "familyMatching", "viking", "classical", "archaic"];

/**
 * The current profile's posted ancestry kit while it has no results: still with the customer or
 * at the lab, or failed, cancelled or lost on the way. The status comes from the account's tests,
 * which the profile pill reads too, so the pages and the pill always agree.
 */
export function useAncestryKit() {
    const barcode = computed(() => store.getters.profile?.profile?.barcode ?? null);

    const test = computed(() => {
        const fromTests = (store.getters.accountTests ?? []).find((t) => t.barcode === barcode.value);
        if (fromTests) return fromTests;
        const selected = store.getters.selectedTest;
        return selected?.barcode && selected.barcode === barcode.value ? selected : null;
    });

    const rawStatus = computed(() => test.value?.raw_status ?? null);

    const pending = computed(() => isAncestryKitPending(barcode.value, rawStatus.value));

    // A pending kit's failed statuses are exactly the ones KIT_FAILURES describes
    const failure = computed(() => {
        const known = pending.value ? KIT_FAILURES[rawStatus.value] : null;
        return known ? { summary: known.lead, ...known } : null;
    });

    const currentStep = computed(() => (pending.value ? currentKitStep(barcode.value, rawStatus.value) : null));

    // An upload instead of a kit, while it has no results: still being tested, or failed
    const uploadPending = computed(() => isUploadWithoutResults(barcode.value, rawStatus.value));

    // Kits sold in the US go to a fulfilment centre before the lab, which adds to the first step
    const viaUsFulfilment = computed(() => test.value?.product_sku?.includes("-US") === true);

    // Whether the kit has any ancestry test on it: some kits are wellbeing only
    const hasAncestryTests = computed(() =>
        ANCESTRY_TESTS.some((type) => store.getters.kitTests?.[type]?.purchased === true)
    );

    return { barcode, test, rawStatus, pending, failure, currentStep, uploadPending, viaUsFulfilment, hasAncestryTests };
}
